import { useEffect, useState } from 'react';

import { useStoreSummary, type StoreSummaryRow } from '../api/queries.js';

/**
 * ВКЛАДКИ МАГАЗИНОВ ВО ВХОДЯЩИХ
 *
 * Оператор обслуживает сразу несколько магазинов аккаунта: на вкладке —
 * сколько диалогов ждут оператора, а точка — что с последнего просмотра
 * вкладки там было новое. Выбранный магазин сужает список и счётчики.
 * Пусто (`''`) — все магазины.
 */

const SEEN_KEY = 'inbx.store_seen';

function readSeen(): Record<string, string> {
  try {
    return JSON.parse(localStorage.getItem(SEEN_KEY) ?? '{}') as Record<string, string>;
  } catch {
    return {};
  }
}

function writeSeen(map: Record<string, string>): void {
  try {
    localStorage.setItem(SEEN_KEY, JSON.stringify(map));
  } catch {
    /* приватный режим: подсветка просто не запомнится */
  }
}

/** Ключ вкладки: магазин или «общие каналы» (без магазина). */
export function storeTabKey(s: Pick<StoreSummaryRow, 'store_id'>): string {
  return s.store_id ?? 'none';
}

export function StoreTabs(props: {
  workspaceSlug: string | undefined;
  value: string;
  onChange: (key: string) => void;
}): JSX.Element | null {
  const summaryQ = useStoreSummary(props.workspaceSlug);
  const [seen, setSeen] = useState<Record<string, string>>(readSeen);
  const rows = summaryQ.data ?? [];

  // Открытая вкладка считается просмотренной — и сейчас, и при новых событиях.
  useEffect(() => {
    if (!props.value) return;
    const row = rows.find((r) => storeTabKey(r) === props.value);
    if (!row?.last_message_at) return;
    if (seen[props.value] && seen[props.value]! >= row.last_message_at) return;
    const next = { ...seen, [props.value]: row.last_message_at };
    setSeen(next);
    writeSeen(next);
  }, [props.value, rows, seen]);

  // Один магазин (или ни одного) — вкладки не нужны.
  if (rows.length < 2) return null;

  const total = rows.reduce((n, r) => n + r.unassigned, 0);
  return (
    <div className="inbx-store-tabs" role="tablist" aria-label="Магазины">
      <button
        type="button"
        role="tab"
        aria-selected={props.value === ''}
        className={`inbx-store-tabs__btn ${props.value === '' ? 'is-active' : ''}`}
        onClick={() => props.onChange('')}
      >
        Все магазины
        {total > 0 && <span className="inbx-store-tabs__badge">{total}</span>}
      </button>
      {rows.map((r) => {
        const key = storeTabKey(r);
        const fresh =
          key !== props.value &&
          !!r.last_message_at &&
          (!seen[key] || seen[key]! < r.last_message_at);
        return (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={props.value === key}
            className={`inbx-store-tabs__btn ${props.value === key ? 'is-active' : ''}`}
            onClick={() => props.onChange(key)}
            title={`В работе: ${r.open}, без оператора: ${r.unassigned}`}
          >
            {fresh && <span className="inbx-store-tabs__dot" aria-label="есть новое" />}
            {r.name}
            {r.unassigned > 0 && <span className="inbx-store-tabs__badge">{r.unassigned}</span>}
          </button>
        );
      })}
    </div>
  );
}
